import type { QboPnl } from '@morales/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney, isIsoDate, todayIso, whenAt } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

/** QuickBooks' section groups, named in the app's words (QuickBooks' own title otherwise). */
const GROUP: Record<string, AppMessageKey> = {
  Income: 'pnlIncome', COGS: 'pnlCogs', GrossProfit: 'pnlGrossProfit', Expenses: 'pnlExpenses', NetOperatingIncome: 'pnlNetOperating',
  OtherIncome: 'pnlOtherIncome', OtherExpenses: 'pnlOtherExpenses', NetOtherIncome: 'pnlNetOther', NetIncome: 'pnlNetIncome',
};

/** The month a day is in, and the periods JL reads the books by. */
const monthStart = (d: string) => `${d.slice(0, 7)}-01`;
const monthEnd = (d: string) => {
  const [y, m] = d.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};
const prevMonth = (d: string) => {
  const [y, m] = d.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 2, 1)).toISOString().slice(0, 10);
};

/** QuickBooks' Profit and Loss, read live (decision 198): by month or one total, each section opening to its accounts. */
export function PnlPage() {
  const { t, intlLocale } = useLocale();
  const today = todayIso();
  const [params, setParams] = useSearchParams();
  const fromRaw = params.get('from') ?? '';
  const toRaw = params.get('to') ?? '';
  const to = isIsoDate(toRaw) && toRaw >= '2000-01-01' ? toRaw : today;
  const from = isIsoDate(fromRaw) && fromRaw >= '2000-01-01' && fromRaw <= to ? fromRaw : monthStart(to);
  const by = params.get('by') === 'month' ? 'month' : 'total';
  const set = (next: Record<string, string>) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) out.set(k, v);
        return out;
      },
      { replace: true },
    );
  const last = prevMonth(today);
  const year = today.slice(0, 4);
  const periods: { key: AppMessageKey; from: string; to: string; by?: 'month' | 'total' }[] = [
    { key: 'pnlThisMonth', from: monthStart(today), to: today, by: 'total' },
    { key: 'pnlLastMonth', from: last, to: monthEnd(last), by: 'total' },
    { key: 'pnlYearToDate', from: `${year}-01-01`, to: today, by: 'month' },
    { key: 'pnlLastYear', from: `${Number(year) - 1}-01-01`, to: `${Number(year) - 1}-12-31`, by: 'month' },
  ];
  const query = useQuery({ queryKey: qk.pnl(from, to, by), queryFn: ({ signal }) => api.pnl({ from, to, by }, signal), placeholderData: keepPreviousData, retry: false, staleTime: 5 * 60_000, refetchOnWindowFocus: false });
  return (
    <div className="stack">
      <PageHead title={t('pnlTitle')} />
      <p className="muted">{t('pnlHint')}</p>
      <div className="toolbar">
        <div className="seg" role="group" aria-label={t('period')}>
          {periods.map((p) => (
            <button key={p.key} type="button" aria-pressed={from === p.from && to === p.to} onClick={() => set({ from: p.from, to: p.to, ...(p.by ? { by: p.by } : {}) })}>
              {t(p.key)}
            </button>
          ))}
        </div>
        <label className="tool">
          <span>{t('fromDate')}</span>
          {/* a From typed after To takes To along with it, never quietly replaced */}
          <input type="date" value={from} max={to} onChange={(e) => e.target.value && set(e.target.value > to ? { from: e.target.value, to: e.target.value } : { from: e.target.value })} />
        </label>
        <label className="tool">
          <span>{t('toDate')}</span>
          <input type="date" value={to} min={from} onChange={(e) => e.target.value && set(e.target.value < from ? { from: e.target.value, to: e.target.value } : { to: e.target.value })} />
        </label>
        <div className="seg" role="group" aria-label={t('pnlColumns')}>
          <button type="button" aria-pressed={by === 'total'} onClick={() => set({ by: 'total' })}>
            {t('pnlOneTotal')}
          </button>
          <button type="button" aria-pressed={by === 'month'} onClick={() => set({ by: 'month' })}>
            {t('pnlByMonth')}
          </button>
        </div>
      </div>
      <QueryView query={query}>
        {(r) => (
          // the last period's numbers stay while the new one is read, dimmed and said
          <div className={query.isPlaceholderData ? 'stale' : undefined} aria-busy={query.isPlaceholderData}>
            {query.isPlaceholderData ? <p className="note">{t('pnlReading')}</p> : null}
            <PnlTable report={r} money={(v) => formatMoney(v, intlLocale)} readAt={whenAt(r.readAt, intlLocale)} />
          </div>
        )}
      </QueryView>
    </div>
  );
}

/** The groups that are a result (gross profit, net income), not a list of accounts */
const RESULT = new Set(['GrossProfit', 'NetOperatingIncome', 'NetOtherIncome', 'NetIncome']);

function PnlTable({ report: r, money, readAt }: { report: QboPnl; money: (v: number) => string; readAt: string }) {
  const { t, intlLocale } = useLocale();
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (i: number) => setOpen((s) => {
    const next = new Set(s);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    return next;
  });
  if (!r.sections.length) return <p className="muted">{t('pnlEmpty')}</p>;
  const income = r.sections.find((s) => s.group === 'Income')?.total ?? [];
  const gross = r.sections.find((s) => s.group === 'GrossProfit')?.total ?? [];
  const pctFmt = new Intl.NumberFormat(intlLocale, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const pct = (i: number) => (income[i] ? pctFmt.format((gross[i] ?? 0) / income[i]!) : '—');
  // a month column in the screen's language (QuickBooks names it in English); the total is the app's word
  const monthFmt = new Intl.DateTimeFormat(intlLocale, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  const head = (c: QboPnl['columns'][number]) => (c.from && c.to ? monthFmt.format(new Date(`${c.from}T12:00:00Z`)) : t('pnlTotal'));
  const basis = r.basis === 'Accrual' ? t('pnlAccrual') : r.basis === 'Cash' ? t('pnlCash') : (r.basis ?? '—');
  const period = `${formatDate(r.from, intlLocale, 'date')} – ${formatDate(r.to, intlLocale, 'date')}`;
  const label = (s: QboPnl['sections'][number]) => (GROUP[s.group] ? t(GROUP[s.group]!) : s.title);
  return (
    <>
      <div className="table-wrap">
        <table className="data-table pnl-table" data-testid="pnl-table">
          <thead>
            <tr>
              <th scope="col">{t('pnlAccount')}</th>
              {r.columns.map((c, i) => (
                <th key={i} scope="col" className="num">
                  {r.by === 'month' ? head(c) : t('pnlTotal')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.sections.map((s, i) => (
              <PnlSection key={i} id={`pnl-s${i}`} label={label(s)} section={s} open={open.has(i)} onToggle={() => toggle(i)} money={money} strong={RESULT.has(s.group)} />
            ))}
            {gross.length ? (
              <tr className="muted" data-testid="pnl-gross-pct">
                <th scope="row">{t('pnlGrossPct')}</th>
                {r.columns.map((_, i) => (
                  <td key={i} className="num">
                    {pct(i)}
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p className="muted">{t('pnlReadAt').replace('{period}', period).replace('{when}', readAt).replace('{basis}', basis)}</p>
    </>
  );
}

function PnlSection({ id, label, section: s, open, onToggle, money, strong }: { id: string; label: string; section: QboPnl['sections'][number]; open: boolean; onToggle: () => void; money: (v: number) => string; strong: boolean }) {
  return (
    <>
      <tr className={strong ? 'total-row' : 'group-row'}>
        <th scope="row">
          {s.lines.length ? (
            <button type="button" className="link-btn" aria-expanded={open} aria-controls={open ? id : undefined} onClick={onToggle}>
              <span aria-hidden="true">{open ? '▾' : '▸'}</span> {label}
            </button>
          ) : (
            label
          )}
        </th>
        {s.total.map((v, i) => (
          <td key={i} className="num">
            {money(v)}
          </td>
        ))}
      </tr>
      {open
        ? s.lines.map((l, j) => (
            <tr key={j} className="sub-row" id={j === 0 ? id : undefined}>
              <th scope="row" className="indent">
                {l.name}
              </th>
              {l.values.map((v, i) => (
                <td key={i} className="num">
                  {v ? money(v) : ''}
                </td>
              ))}
            </tr>
          ))
        : null}
    </>
  );
}
